/**
 * PremiumPage — Gestion des accès Premium (S5.4-13 étendu)
 * Liste les utilisateurs Premium, permet l'ajout/retrait d'accès.
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldOff } from 'lucide-react';
import { useSDK } from '../context/SDKContext.tsx';
import type { PremiumUser } from '@assistia/sdk';

export default function PremiumPage(): React.ReactElement {
  const { sdk } = useSDK();
  const [users, setUsers] = useState<PremiumUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    sdk.finops.listPremiumUsers()
      .then(({ data }) => setUsers(data))
      .catch(err => setError((err as Error).message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [sdk]);

  const revoke = async (userId: string) => {
    setActionId(userId);
    try {
      await sdk.admin.revokePremium(userId);
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Accès Premium</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Utilisateurs disposant d&apos;un accès aux modèles premium (Mistral Large, etc.)
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Chargement…</div>
      ) : users.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Aucun utilisateur Premium pour l&apos;instant.
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Utilisateur</th>
                <th className="px-4 py-3 font-medium">Modèles autorisés</th>
                <th className="px-4 py-3 font-medium">Accordé le</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.user_id}
                  className="border-t border-border hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={14} className="text-primary shrink-0" />
                      <span className="font-medium text-foreground">{u.email}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {u.model_access.map(m => (
                        <span
                          key={m}
                          className="inline-block px-2 py-0.5 rounded text-xs bg-primary/10 text-primary font-mono"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(u.granted_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => revoke(u.user_id)}
                      disabled={actionId === u.user_id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border border-destructive/30 text-destructive hover:bg-destructive/10 disabled:opacity-50 transition-colors"
                    >
                      <ShieldOff size={12} />
                      {actionId === u.user_id ? 'En cours…' : 'Révoquer'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
